import React, { useEffect } from 'react';
import { Notification, STATUS_TYPES, generateResolver, yup } from 'dyl-components';
import { Button, Form } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomGroupUtils } from 'utils';

import { Controller, useForm } from 'react-hook-form';

import customFieldsGroupActions from "actions/custom_fields_group";
import pipelineActions from 'actions/pipeline';
import MasterSecondarySourceOptions from './MasterSecondarySourceOptions';
import campaignsAction from "actions/campaigns";

import sequenceTasksActions from 'actions/sequence_tasks';
import productInterestActions from "actions/product_interest";

const ConvertContactForm = ({
    onClose,

    account_id,
    contact_id,

    size,
    refresh,

    onModify,
    campaign_id
}) => {
    const { close_reason_id, isCreating, group, isReadingCategories, isReadingCustomFields, pipelineCategories, campaignConvertedOptions, isReadingSequenceContactTasks, isReadingProductInterestOptions, productInterestOptions, userAuthTimezone } = useSelector(state => {
        return {
            isCreating: state.pipeline.isConvertingContact,
            isReadingCustomFields: state.custom_fields_group.isReadingModalStandardFieldGroup,
            group: state.custom_fields_group.modal_standard_group,
            productInterestOptions: state.product_interests.productInterestOptions,
            isReadingProductInterestOptions: state.product_interests.isReadingProductInterestOptions,
            selectedSequenceId: state.sequence_tasks.selectedSequenceId,
            pipelineCategories: state.pipeline.categories.map(category => ({
                key: category.id,
                value: category.id,
                text: category.name,
                stages: category.stages?.map(stage => ({
                    key: stage.id,
                    value: stage.id,
                    text: stage.name,
                    sequence_id: stage.sequence_id,
                })) || [],
                isDefault: category.primary
            })),
            isReadingCategories: state.pipeline.isReadingCategories,
    
            close_reason_id: state.contact.lead?.close_reason?.id,
            campaignConvertedOptions: state.campaigns.campaignConvertedOptions.map((campaign) => ({key: campaign.id, value: campaign.id, text: campaign.name})),
            isReadingSequenceContactTasks: state.sequence_tasks.isReadingSequenceContactTasks,
            userAuthTimezone: state.auth.timezone
        };
    });

    const fields = group?.fields?.data || [];

    const { formState: { isDirty, isValid }, control, setValue, handleSubmit, watch, } = useForm({
        mode: 'onChange',
        defaultValues: {
            pipeline: null,
            stage: null,
            type: null,
            product_interests: [],
            expected_revenue: '',
            close_probability: '',
            master_source: null,
            secondary_source: null,
            priority: null,
            rating: null,
            score: null,
            acquisition_cost: null,
            additional_cost: null,
            lead_custom_data: [],
            campaign_converted: campaign_id || undefined
        },
        resolver: generateResolver({
            pipeline: yup.string().required('This field is required'),
            stage: yup.string().required('This field is required')
        })
    });

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(customFieldsGroupActions.getModalStandardGroup({ group_name: 'lead' }));
        dispatch(pipelineActions.getPipelineCategories({ account_stage: "lead" }));
        dispatch(campaignsAction.readPersonCampaignConverted({ person_id: contact_id, campaign_conversion: "lead" }));
        dispatch(sequenceTasksActions.readContactTasks(contact_id));
        dispatch(productInterestActions.readProductInterestOptions());
    }, [dispatch, contact_id]);

    useEffect(() => {
        onModify(isDirty);
    }, [isDirty, onModify]);

    const [watchedPipeline] = watch(['pipeline']);

    const onCreate = async (data) => {
        const custom_data = {
            fields: CustomGroupUtils.extractLeadDetails(data),
            children: {}
        };

        const { stage, master_source, secondary_source, campaign_converted, pipeline, product_interests } = data;
        const category = pipelineCategories.find(category => category.value === Number(pipeline));
        const newStage = category?.stages.find(categoryStage => categoryStage.value === Number(stage));
        try {
            await dispatch(pipelineActions.convertContact({
                account_id,
                contact_id,
                
                custom_data,
                pipeline_stage_id: Number(stage),
                close_reason_id,
                master_source,
                campaign_id: campaignConvertedOptions?.map(campaign => campaign.value)?.includes(campaign_converted) ? campaign_converted : undefined,
                secondary_source_id: secondary_source,
                product_interest_ids: product_interests,
                timezone: userAuthTimezone,
                sequence_id: newStage?.sequence_id
            }));
            Notification.alert('Successfully converted to lead!', STATUS_TYPES.SUCCESS);
            onClose();
            if (refresh) {
                refresh();
            }
        } catch (e) {
            console.log(e);
            Notification.alert('Failed to convert to lead', STATUS_TYPES.ERROR);
        }
    }

    useEffect(() => {
        const defaultPipeline = pipelineCategories.find(category => category.isDefault);

        if (defaultPipeline && watch('pipeline') === null && watch('stage') === null && !isReadingCategories) {
            setValue('pipeline', defaultPipeline.value, {
                shouldDirty: true,
                shouldValidate: true
            });

            setValue('stage', defaultPipeline.stages[0].value, {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [pipelineCategories, setValue, watch, isReadingCategories]);

    return [
        <Form loading={isCreating || isReadingSequenceContactTasks} size={size} noValidate style={{ maxHeight: "17em", overflowY: "scroll", overflowX: "hidden", paddingRight: "1em", marginBottom: "1em" }}>
            <Form.Group widths='equal'>
                <Controller
                    name='pipeline'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Pipeline'
                            required
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => {
                                onChange({ target: { name, value } });
                                setValue('stage', pipelineCategories.find(category => category.value === value)?.stages[0]?.value || null, { shouldValidate: true });
                            }}
                            options={pipelineCategories}
                            search
                            selectOnBlur={false}
                            placeholder='Select Pipeline'
                            loading={isReadingCategories}
                        />
                    )}
                />
                <Controller
                    name='stage'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Stage'
                            required
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            options={pipelineCategories.find(category => category.value === watchedPipeline)?.stages || []}
                            search
                            selectOnBlur={false}
                            placeholder='Select Stage'
                        />
                    )}
                />
            </Form.Group>
            <Form.Group widths={'equal'}>
                <Controller
                    name='type'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Type'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            options={CustomGroupUtils.getFieldOptions(fields, name)}
                            search
                            selectOnBlur={false}
                            placeholder='Select Type'
                            clearable
                            loading={isReadingCustomFields}
                        />
                    )}
                />
                <Controller
                    name='product_interests'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Product Interests'
                            multiple
                            search
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            selectOnBlur={false}
                            placeholder='Select Product Interest(s)'
                            options={productInterestOptions.map(productInterestOption => (
                                { key: productInterestOption.id, value: productInterestOption.id, text: productInterestOption.value }
                            ))}
                            disabled={isReadingProductInterestOptions}
                            loading={isReadingProductInterestOptions}
                        />
                    )}
                />
            </Form.Group>
            <Form.Group widths={'equal'}>
                <Controller
                    name='expected_revenue'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Input
                            label='Expected Revenue'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            placeholder='Enter Expected Revenue'
                        />
                    )}
                />
                <Controller
                    name='close_probability'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Input
                            label='Close Probability'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            placeholder='Enter Close Probability'
                        />
                    )}
                />
            </Form.Group>
            <Form.Group>
                <Controller
                    name='master_source'
                    control={control}
                    render={({ field: { name: masterSourceFieldName, value: masterSourceFieldValue, onChange: masterSourceOnChange } }) => (
                        <Controller
                            name='secondary_source'
                            control={control}
                            render={({ field: { name: secondarySourceFieldName, value: secondarySourceValue, onChange: secondarySourceOnChange } }) => (
                                <Form.Field
                                    control={MasterSecondarySourceOptions}
                                    onChange={(_, { parent_value: master_source, child_value: secondary_source }) => {
                                        masterSourceOnChange({ target: { name: masterSourceFieldName, value: master_source } });
                                        secondarySourceOnChange({ target: { name: secondarySourceFieldName, value: secondary_source } });
                                    }}
                                    value={secondarySourceValue}
                                    parentValue={masterSourceFieldValue}
                                    label='Master/Secondary Source'
                                    placeholder='Select Source'
                                    width={8}
                                />
                            )}
                        />
                    )}
                />
                <Controller
                    name={'campaign_converted'}
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            fluid
                            label='Campaign Converted'
                            name={name}
                            value={value}
                            onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                            options={campaignConvertedOptions}
                            search
                            selectOnBlur={false}
                            placeholder='Select Campaign'
                            width={8}
                            disabled={campaign_id}
                        />
                    )}
                />
            </Form.Group>
            <Form.Group widths={'equal'}>
                <Controller
                    name='priority'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Priority'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            options={CustomGroupUtils.getFieldOptions(fields, name)}
                            search
                            selectOnBlur={false}
                            placeholder='Select Lead Priority'
                            clearable
                            loading={isReadingCustomFields}
                        />
                    )}
                />
                <Controller
                    name='rating'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Rating'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            options={CustomGroupUtils.getFieldOptions(fields, name)}
                            search
                            selectOnBlur={false}
                            placeholder='Select Lead Rating'
                            clearable
                            loading={isReadingCustomFields}
                        />
                    )}
                />
            </Form.Group>
            <Form.Group widths={'equal'}>
                <Controller
                    name='score'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Input
                            label='Score'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            placeholder='Enter Lead Score'
                        />
                    )}
                />
                <Controller
                    name='acquisition_cost'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Input
                            label='Acquisition Cost'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            placeholder='Enter Acquisition Cost'
                        />
                    )}
                />
            </Form.Group>
            <Form.Group>
                <Controller
                    name='additional_cost'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Input
                            label='Additional Costs'
                            name={name}
                            value={value}
                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                            placeholder='Enter Additional Costs'
                        />
                    )}
                />
            </Form.Group>
        </Form>,
        <Button floated='right' disabled={!isDirty || !isValid || isCreating} primary onClick={handleSubmit(onCreate)} loading={isCreating}>Save</Button>
    ]
}

export default ConvertContactForm;
